import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './RankMatchSetup.css';
import PageButton from '../elements/PageButton/PageButton';
import RankedMatchButton from '../RankedMatchButton/RankedMatchButton';
import RankMatchOpponentCountsModal from './RankMatchOpponentCountsModal';
import RankLeaguesTable from './RankLeaguesTable';
import RankMatchRewardsInfo, {RankMatchRewardMultipliers} from './RankMatchRewardsInfo';
import {API_BASE} from '../../utils/apiConfig';
import {RANK_MATCH_OPPONENT_COUNT_OPTIONS} from '../../configs/rankMatch';
import {getRankMatchOpponentCountsSummary} from '../../utils/lobby/getRankMatchOpponentCountsSummary';
import {usePageAnalytics} from '../../hooks/analytics/usePageAnalytics';
import {MENU_SOUND_MANAGER} from '../../scenes/MenuSoundManager';
import {showConstructionPhaseInfoModal} from '../../redux/actions';
import {RULES_SECTION_IDS} from '../Modals/RulesModal/rulesSections';
import {useDesktopLayout} from '../../hooks/visuals/useWideScreenLayout';

const PAGE_ID = 'RankMatchSetup';

/**
 * Экран настройки поиска рейтингового матча — между кнопкой «Рейтинговый
 * матч» главного меню и самим поиском (RankMatchWaiting). Здесь и только здесь
 * игрок задает ограничение длительности матча (по числу противников, см.
 * configs/rankMatch.js): во время поиска оно лишь показывается. Ниже — справка:
 * лиги со ссылкой на раздел правил «Рейтинг» и награды рейтингового матча.
 *
 * onStartSearch(opponentCounts) запускает поиск (App.handleRankedGame);
 * opponentCounts — null, если сохраненный выбор так и не загрузился: тогда
 * сервер возьмет его из профиля сам.
 */
function RankMatchSetup({onStartSearch, onBack}) {
  usePageAnalytics(PAGE_ID);

  const dispatch = useDispatch();
  const auth = useSelector((state) => state.private.auth);
  // Множители наград: на десктопе — в левой колонке под «Начать поиск», на
  // телефоне — последним блоком, под «Наградами» (иначе между кнопками и
  // справкой стоит блок, который читают уже после нее)
  const isDesktopLayout = useDesktopLayout();
  // null — сохраненное в профиле ограничение еще не загружено (или не
  // загрузилось): кнопка показывает только заголовок, и поиск не перетирает
  // профиль догадкой «без ограничений»
  const [opponentCounts, setOpponentCounts] = useState(null);
  const [isOpponentCountsModalVisible, setIsOpponentCountsModalVisible] = useState(false);
  const opponentCountsRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    fetch(`${API_BASE}/rank-match/opponent-counts`, {credentials: 'include'})
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        // Игрок успел поменять выбор раньше ответа — его выбор важнее
        if (!mountedRef.current || opponentCountsRef.current !== null) return;
        const loaded = Array.isArray(data.opponentCounts) && data.opponentCounts.length > 0
          ? data.opponentCounts
          : RANK_MATCH_OPPONENT_COUNT_OPTIONS;
        opponentCountsRef.current = loaded;
        setOpponentCounts(loaded);
      })
      .catch((error) => {
        console.error('[RankMatchSetup] Failed to load opponent counts', error);
      });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Оптимистичное применение выбора + немедленное сохранение в профиль; при
  // ошибке — откат к прежнему выбору. Выбор к тому же уходит и вместе со входом
  // в поиск (см. handleStartSearch), так что гонки «поменял и сразу нажал
  // „Начать поиск“» нет
  const handleOpponentCountsChange = useCallback((nextOpponentCounts) => {
    const prevOpponentCounts = opponentCountsRef.current;
    opponentCountsRef.current = nextOpponentCounts;
    setOpponentCounts(nextOpponentCounts);
    fetch(`${API_BASE}/rank-match/opponent-counts`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({playerId: auth.id, opponentCounts: nextOpponentCounts}),
    }).then((res) => {
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
    }).catch((error) => {
      console.error('[RankMatchSetup] Failed to update opponent counts', error);
      if (mountedRef.current) {
        opponentCountsRef.current = prevOpponentCounts;
        setOpponentCounts(prevOpponentCounts);
      }
    });
  }, [auth.id]);

  const handleStartSearch = useCallback(() => {
    onStartSearch(opponentCountsRef.current);
  }, [onStartSearch]);

  const handleOpenRules = useCallback(() => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    dispatch(showConstructionPhaseInfoModal(RULES_SECTION_IDS.RANKED));
  }, [dispatch]);

  const summary = opponentCounts ? getRankMatchOpponentCountsSummary(opponentCounts) : null;

  const multipliersSection = (
    <section className="rank-match-setup_section rank-match-setup_multipliers">
      <h2 className="rank-match-setup_section-title">Множители наград</h2>
      <RankMatchRewardMultipliers/>
    </section>
  );

  return (
    <div className="rank-match-setup">
      <h1 className="rank-match-setup_title">Рейтинговый матч</h1>

      <div className="rank-match-setup_layout">
        <div className="rank-match-setup_main">
          <section className="rank-match-setup_section">
            <button
              className="rank-match-opponent-counts-button"
              onClick={() => {
                MENU_SOUND_MANAGER.playMenuButtonClick();
                setIsOpponentCountsModalVisible(true);
              }}
            >
              <span className="rank-match-opponent-counts-button_title">Ограничить длительность матча</span>
              {summary ? (
                <>
                  <span className="rank-match-opponent-counts-button_value">{summary.opponentsText}</span>
                  <span className="rank-match-opponent-counts-button_value">{summary.durationText}</span>
                  <span className="rank-match-opponent-counts-warning text-attention">
                    Время ожидания может быть увеличено
                  </span>
                </>
              ) : (
                opponentCounts && (
                  <span className="rank-match-opponent-counts-button_value">Сейчас: без ограничений</span>
                )
              )}
            </button>
            <p className="rank-match-setup_note rank-match-setup_note_centered">
              Во время поиска ограничение изменить нельзя — только отменить поиск.
            </p>
          </section>

          <div className="main-menu-buttons rank-match-setup_buttons">
            <RankedMatchButton
              label="Начать поиск"
              onClick={handleStartSearch}
              pageId={PAGE_ID}
              buttonId="start_search"
            />
            <PageButton className="main-menu-button_secondary" onClick={onBack} pageId={PAGE_ID} buttonId="back">
              Назад
            </PageButton>
          </div>

          {isDesktopLayout && multipliersSection}
        </div>

        <div className="rank-match-setup_info">
          <section className="rank-match-setup_section">
            <RankLeaguesTable rating={auth.rating ?? 0}/>
            <button type="button" className="rank-match-setup_rules-link" onClick={handleOpenRules}>
              <span className="rank-match-setup_rules-link-icon" aria-hidden="true"/>
              Как начисляется рейтинг и какие боты встречаются
            </button>
          </section>

          <section className="rank-match-setup_section">
            <h2 className="rank-match-setup_section-title">Награды</h2>
            <RankMatchRewardsInfo/>
          </section>

          {!isDesktopLayout && multipliersSection}
        </div>
      </div>

      {isOpponentCountsModalVisible && (
        <RankMatchOpponentCountsModal
          // Без загруженного выбора модалка стартует с «без ограничений»; в
          // профиль это уйдет, только если игрок сам что-то поменяет
          opponentCounts={opponentCounts || RANK_MATCH_OPPONENT_COUNT_OPTIONS}
          onChange={handleOpponentCountsChange}
          handleClose={() => setIsOpponentCountsModalVisible(false)}
        />
      )}
    </div>
  );
}

export default RankMatchSetup;
